import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';

import classnames from 'classnames';
import { intersection } from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { Comment, PublishStatus, SystemLabel, ResponseFailedReason } from 'common/comments2/Comments2.types';

import 'inbox/status-message/StatusMessage.scss';

interface Props {
    message: Comment;
    retry?: () => void;
    currentConversation?: Comment;
}

export enum PublishStatusType {
    Failure,
    Success,
}

interface PublishStatusOption {
    type: PublishStatusType;
    retryable: boolean;
    icon?: string;
    message?: string;
    tooltip?: string;
}

export const getPublishStatusOption = (
    status: PublishStatus,
    responseFailedReason?: ResponseFailedReason,
    responseFailedReasonExplanation?: string,
): PublishStatusOption | undefined => {
    const message = 'PUBLISH_STATUS_GENERIC_FAILURE';

    switch (status) {
        case PublishStatus.Success:
        case PublishStatus.Queued: {
            return { type: PublishStatusType.Success, retryable: false };
        }
        case PublishStatus.Pending: {
            return {
                type: PublishStatusType.Success,
                retryable: false,
                message: 'PUBLISH_STATUS_PENDING',
                tooltip: 'PUBLISH_STATUS_PENDING',
                icon: 'icon-hourglass',
            };
        }
        case PublishStatus.PendingManual: {
            return {
                type: PublishStatusType.Success,
                retryable: false,
                message: 'PUBLISH_STATUS_PENDING_MANUAL',
                tooltip: 'PUBLISH_STATUS_PENDING_MANUAL',
                icon: 'icon-hourglass',
            };
        }
        case PublishStatus.Posted: {
            return {
                type: PublishStatusType.Success,
                retryable: false,
                message: 'PUBLISH_STATUS_POSTED',
                tooltip: 'PUBLISH_STATUS_POSTED',
                icon: 'icon-check',
            };
        }
        case PublishStatus.Duplicate:
        case PublishStatus.AuthorizationInvalid:
        case PublishStatus.AuthorizationRevoked:
        case PublishStatus.AuthorizationExpired:
        case PublishStatus.AuthorizationRejected:
        case PublishStatus.InsufficientPermission:
        case PublishStatus.DataValidationError:
        case PublishStatus.DuplicateError:
        case PublishStatus.LocationSetupError:
        case PublishStatus.CredentialNotFound:
        case PublishStatus.RejectedContent:
        case PublishStatus.MediaResourceError:
        case PublishStatus.MediaRejectedContent:
        case PublishStatus.TemplatingError:
        case PublishStatus.TenantModuleAccessMissing:
        case PublishStatus.SchedulingError:
        case PublishStatus.PhoneNumberBlocked:
        case PublishStatus.PhoneNumberInvalid:
        case PublishStatus.AccountBlocked:
        case PublishStatus.BadRequest:
        case PublishStatus.NotFound:
        case PublishStatus.ObjectDeleted:
        case PublishStatus.Unknown:
        case PublishStatus.InternalServerError:
        case PublishStatus.ServiceUnavailable:
        case PublishStatus.GatewayTimeout:
        case PublishStatus.ThirdPartyApiDown:
        case PublishStatus.PublishServiceError:
        case PublishStatus.AppRateLimitExceeded:
        case PublishStatus.UserRateLimitExceeded:
        case PublishStatus.MediaProcessingError:
        case PublishStatus.Profanity: {
            let tooltip;

            if (responseFailedReason) {
                let responseMessage;

                switch (responseFailedReason) {
                    case ResponseFailedReason.ClientRequest:
                        responseMessage = 'CLIENT_REQUEST';
                        break;
                    case ResponseFailedReason.CredentialsMissing:
                        responseMessage = 'CREDENTIALS_MISSING';
                        break;
                    case ResponseFailedReason.CredentialsInvalid:
                        responseMessage = 'CREDENTIALS_INVALID';
                        break;
                    case ResponseFailedReason.ReviewDeleted:
                        responseMessage = 'REVIEW_DELETED';
                        break;
                    case ResponseFailedReason.Other:
                        responseMessage = responseFailedReasonExplanation;
                        break;
                    default:
                        responseMessage = message;
                }
                return {
                    type: PublishStatusType.Failure,
                    icon: 'icon-exclamation-circle',
                    retryable: false,
                    message: responseMessage,
                };
            }

            switch (status) {
                case PublishStatus.ObjectDeleted:
                    return {
                        type: PublishStatusType.Failure,
                        icon: 'icon-exclamation-circle',
                        message,
                        tooltip: message,
                        retryable: false,
                    };
                case PublishStatus.Unknown:
                    tooltip = 'PUBLISH_STATUS_UNKNOWN';
                    break;
                case PublishStatus.InternalServerError:
                    tooltip = 'PUBLISH_STATUS_INTERNAL_ERROR';
                    break;
                case PublishStatus.ServiceUnavailable:
                    tooltip = 'PUBLISH_STATUS_SERVICE_UNVAILABLE';
                    break;
                case PublishStatus.GatewayTimeout:
                    tooltip = 'PUBLISH_STATUS_GATEWAY_TIMEOUT';
                    break;
                case PublishStatus.ThirdPartyApiDown:
                    tooltip = 'PUBLISH_STATUS_API_DOWN';
                    break;
                case PublishStatus.PublishServiceError:
                    tooltip = 'PUBLISH_STATUS_SERVICE_ERROR';
                    break;
                case PublishStatus.AppRateLimitExceeded:
                    tooltip = 'PUBLISH_STATUS_RATE_LIMIT';
                    break;
                case PublishStatus.UserRateLimitExceeded:
                    tooltip = 'PUBLISH_STATUS_USER_RATE_LIMIT';
                    break;
                case PublishStatus.Profanity:
                    tooltip = 'PUBLISH_STATUS_PROFANITY';
                    break;
                default:
                    tooltip = 'PUBLISH_STATUS_MEDIA_ERROR';
                    break;
            }

            return {
                type: PublishStatusType.Failure,
                icon: 'icon-exclamation-circle',
                retryable: true,
                message,
                tooltip,
            };
        }

        default: {
            console.info(`unhandled publish status ${status}`);

            return {
                type: PublishStatusType.Failure,
                retryable: false,
                message,
            };
        }
    }
};

export const PublishStatusMessage: FunctionComponent<Props> = ({
    retry,
    currentConversation,
    message: { publishStatus = PublishStatus.Success, responseFailedReason, responseFailedReasonExplanation },
}) => {
    const { t } = useTranslation('inbox');
    const [statusClassnames, setStatusClassnames] = useState<string>();
    const currentStatus = getPublishStatusOption(publishStatus, responseFailedReason, responseFailedReasonExplanation);

    useEffect(() => {
        if (currentStatus) {
            setStatusClassnames(
                classnames({
                    'status-success': currentStatus.type === PublishStatusType.Success,
                    'status-failure': currentStatus.type === PublishStatusType.Failure,
                    'status-retryable': currentStatus.retryable,
                }),
            );
        }
    }, [currentStatus]);

    const convLabelsIncludes = useCallback(
        (...args): boolean => {
            if (!currentConversation) {
                return false;
            }

            return !!intersection(currentConversation?.labels, args).length;
        },
        [currentConversation],
    );

    if (!currentStatus) {
        return null;
    }

    return (
        <div className="r4e-inbox-status-message">
            {currentStatus.icon && (
                <OverlayTrigger
                    overlay={<Tooltip id="tooltip">{currentStatus.tooltip && t(currentStatus.tooltip)}</Tooltip>}
                >
                    <i className={`${currentStatus.icon} ${statusClassnames}`} />
                </OverlayTrigger>
            )}

            {currentStatus.message && (
                <span className={`status-message ${statusClassnames}`}>{t(currentStatus.message)}</span>
            )}
            {currentStatus.retryable && retry && !convLabelsIncludes(SystemLabel.Block, SystemLabel.Locked) && (
                <OverlayTrigger overlay={<Tooltip id="tooltip">{t('RETRY')}</Tooltip>}>
                    <i className="icon-redo text-muted retry" role="presentation" onClick={retry} />
                </OverlayTrigger>
            )}
        </div>
    );
};
